/**
 * offline_tap_controller.js — show a time tap queued offline on every card it
 * changes, until the server applies it (Stimulus).
 *
 * Start a visit on the job page with no signal, open the dashboard, and its
 * clock card must say On shift, not offer Clock on. Jinja is the only renderer
 * (ADR-010), so each time card draws every state it can be in, the one the
 * server knows shown and the rest hidden (tap_toggle in
 * templates/macros/time_fields.html), and names the taps that lead to each,
 * from TimeEntryService.tap_states. The device is told that starting a visit
 * puts someone on shift; it never works it out.
 *
 * Of the device's queued edits (_intent_watch.js), this reads only time taps
 * and shows the state the latest one for this card leads to:
 *
 *   pending  still on the device: that state, with "Waiting to sync". Its
 *            button is a real form, so the next tap queues too.
 *   synced   applied while this card was on screen: that state, "Synced".
 *   refused  refused while this card was on screen: the server's state, with
 *            the server's words.
 *
 * A tap already applied before the card was drawn counts for nothing: the
 * card was drawn after it, or the page check swaps in one that was.
 *
 * It toggles `hidden` and sets text on the elements the macro drew. It never
 * draws markup (tests/test_powersync_presentation_ssot.py).
 */

import { Controller } from "@hotwired/stimulus";
import { intents, outcomes, pendingIds, STATUS_TEXT, subscribe, unsubscribe } from "./_intent_watch.js";

/** The state of ``card`` a tap on ``target`` ("start_visit:42") leads to, if any. */
function stateReached(card, target) {
    const [verb, visit] = target.split(":");
    if (visit && card.visit && visit !== card.visit) return null;
    return Object.keys(card.states).find((name) => card.states[name].includes(verb)) || null;
}

/**
 * What ``card`` shows now: { state, kind, message }. ``card`` is
 * { current, states, visit, since }: the server's state, the taps leading to
 * each state, the visit it belongs to ('' for the shift), and when it was
 * drawn (device ms), before which an outcome is already in the drawing.
 */
export function tapState(card, intentRows, pending, outcomeRows) {
    let latest = null;
    intentRows.forEach((intent, id) => {
        if (intent.operation !== "time") return;
        const state = stateReached(card, intent.target);
        const outcome = outcomeRows.get(id);
        const live = pending.has(id) || (outcome && outcome.at >= card.since);
        if (!state || !live) return;
        if (!latest || intent.requestedAt >= latest.requestedAt) {
            latest = { state, outcome, pending: pending.has(id), requestedAt: intent.requestedAt };
        }
    });
    if (latest?.outcome && !latest.outcome.applied) {
        return { state: card.current, kind: "refused", message: latest.outcome.message || "" };
    }
    if (!latest || latest.state === card.current) return { state: card.current, kind: "", message: "" };
    return { state: latest.state, kind: latest.pending ? "pending" : "synced", message: "" };
}

export default class extends Controller {
    static targets = ["state", "status"];
    static values = { current: String, states: Object, visit: String };

    connect() {
        this._since = Date.now();
        this._onChange = () => this.refresh();
        subscribe(this._onChange);
        this.refresh();
    }

    disconnect() {
        unsubscribe(this._onChange);
    }

    refresh() {
        const card = {
            current: this.currentValue, states: this.statesValue, visit: this.visitValue, since: this._since,
        };
        const { state, kind, message } = tapState(card, intents, pendingIds, outcomes);
        this.stateTargets.forEach((side) => { side.hidden = side.dataset.stateName !== state; });
        const status = kind === "refused" ? `Not saved: ${message}` : (STATUS_TEXT[kind] || "");
        this.statusTarget.textContent = status;
        this.statusTarget.hidden = !status;
        this.statusTarget.dataset.state = kind;
    }
}
